<template>
  <dialog-modal-fi
    ref="retailerSearchDialog"
    class="retail-search-dialog-fi"
    large-padding
    lazy
    prevent-close-on-click-outside
  >
    <template #header>
      <h2 class="retail-search-dialog-fi__headline">
        {{ $dict.get('dealer-Locator.Headline') || fields?.retailerSearchHeadline?.value }}
      </h2>

      <richtext-fi
        v-if="description"
        class="retail-search-dialog-fi__description"
        :html-content="description"
      />
    </template>

    <template #body>
      <div class="retail-search-dialog-fi__retailer-search">
        <retailer-search-fi
          ref="retailerSearch"
          theme="modal"
          :country-list="(countryList[1] as any) ?? []"
          :default-country="(countryList[2] as string | undefined) ?? undefined"
          :ranges="ranges"
          :default-range="defaultRange"
          :product-ids="catalogId ?? undefined"
        />
      </div>
    </template>
  </dialog-modal-fi>
</template>

<script setup lang="ts">
import { DialogModalFi, RichtextFi } from 'atoms';
import { computed, defineAsyncComponent, inject, useTemplateRef } from 'vue';

import { useDistance } from '@/composables/distance';
import type { ItemLinkField, RichTextField, SingleLineTextField } from '@/sitecoreFieldTypes';
import { getSelectList } from '@/utils/fieldTransformations';

const RetailerSearchFi = defineAsyncComponent(
  () => import('@/components/retailer-search-fi/RetailerSearchFi.vue'),
);

interface RetailSearchFields {
  retailerSearchHeadline?: SingleLineTextField;
  retailerSearchDescription?: RichTextField;
  retailerSearchRanges?: SingleLineTextField;
  retailerSearchDefaultRange?: SingleLineTextField;
  countryList?: ItemLinkField;
  [key: string]: any; // compatibility with 'fieldsToUse'
}

interface Props {
  fields?: RetailSearchFields | null;
  catalogId?: string | null;
  productName?: string | null;
}

defineOptions({
  name: 'RetailSearchDialogFi',
});

const props = withDefaults(defineProps<Props>(), {
  fields: null,
  catalogId: null,
  productName: null,
});

const { unit } = useDistance();

const retailerSearchDialog = useTemplateRef('retailerSearchDialog');

const injectedFields = inject<RetailSearchFields>('fields');

// Use props.fields or fallback to injected fields
const fieldsToUse = computed(() => props.fields ?? injectedFields);

const countryList = computed(() => {
  if (!fieldsToUse.value) {
    return [null, [], undefined];
  }
  return getSelectList(fieldsToUse.value, 'countryList', []);
});

const ranges = computed(() => {
  const rangeList: Array<{ name: string; value: string }> = [];
  if (fieldsToUse.value?.retailerSearchRanges?.value) {
    fieldsToUse.value.retailerSearchRanges.value.split(',').forEach((range: string) => {
      rangeList.push({ name: `${range} ${unit.value}`, value: range });
    });
  }

  return rangeList.length ? rangeList : undefined;
});

const defaultRange = computed(() => {
  if (fieldsToUse.value?.retailerSearchDefaultRange?.value) {
    return fieldsToUse.value.retailerSearchDefaultRange.value;
  }

  return undefined;
});

const description = computed(() => {
  if (fieldsToUse.value?.retailerSearchDescription?.value) {
    return {
      value: fieldsToUse.value.retailerSearchDescription.value
        .replace('{0}', String(Number(props.catalogId)))
        .replace('{1}', props.productName || ''),
    };
  }

  return null;
});

const show = () => {
  retailerSearchDialog.value?.open();
};

defineExpose({
  show,
});
</script>

<style scoped src="./retail-search-dialog-fi" />
